import React, { useRef, useState } from 'react'
import { Button, Form, FormText, Spinner } from 'reactstrap'
import CredentialsFields from './CredentialsFields'

// Name and size together, because two sheets from different runs are often both called
// "questions.csv". The same file chosen twice is still one file.
function fileKey(file) {
    return `${file.name}:${file.size}`
}

function UploadForm({ onUpload, uploading, userId, password, setUserId, setPassword }) {
    // A run leaves two sheets, the questions and the assumed values, and either can come back
    // answered. They open into one review, so both are loaded at once rather than a round trip each.
    const [files, setFiles] = useState([])
    const [touched, setTouched] = useState(false)
    const fileInput = useRef(null)

    const errors = {
        userId: userId.trim() ? null : 'Required',
        password: password ? null : 'Required',
        file: files.length ? null : 'Choose a sheet to load',
    }
    const invalid = Object.values(errors).some(Boolean)

    const submit = event => {
        event.preventDefault()
        setTouched(true)
        if (invalid) return
        onUpload({ userId: userId.trim(), password }, files)
    }

    const addFiles = chosen => setFiles(current => {
        const seen = new Set(current.map(fileKey))
        return [...current, ...[...chosen].filter(file => !seen.has(fileKey(file)))]
    })

    const removeFile = key => setFiles(current => current.filter(file => fileKey(file) !== key))

    const openFilePicker = () => fileInput.current && fileInput.current.click()

    return (
        <div>
            <p className="text-muted small mb-4">
                Load the sheets from an earlier run with answers filled in — questions,
                assumed, or both. Nothing is read from OpenDock and nothing is written
                until Apply.
            </p>

            <Form onSubmit={submit} noValidate style={{ maxWidth: 480 }}>
                <fieldset className="mb-4" disabled={uploading}>
                    <legend className="odo-legend">OpenDock account</legend>
                    <CredentialsFields
                        idPrefix="odo-up"
                        userId={userId}
                        password={password}
                        setUserId={setUserId}
                        setPassword={setPassword}
                        errors={errors}
                        touched={touched}
                    />
                    <FormText>Apply signs in again, so credentials are required.</FormText>
                </fieldset>

                <fieldset className="mb-4" disabled={uploading}>
                    <legend className="odo-legend">The sheet</legend>
                    <div
                        className={`odo-drop ${touched && errors.file ? 'is-invalid-drop' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={openFilePicker}
                        onKeyDown={e => e.key === 'Enter' && openFilePicker()}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => {
                            e.preventDefault()
                            if (e.dataTransfer.files) addFiles(e.dataTransfer.files)
                        }}
                    >
                        {files.length ? (
                            <>
                                {files.map(file => (
                                    <div
                                        key={fileKey(file)}
                                        className="d-flex justify-content-between align-items-center"
                                    >
                                        <span className="fw-semibold">{file.name}</span>
                                        <span className="text-muted small">
                                            {(file.size / 1024).toFixed(1)} KB
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-link text-muted"
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    removeFile(fileKey(file))
                                                }}
                                            >
                                                remove
                                            </button>
                                        </span>
                                    </div>
                                ))}
                                <div className="text-muted small">Click to add another</div>
                            </>
                        ) : (
                            <>
                                <div className="fw-semibold">
                                    Choose the sheets, or drop them here
                                </div>
                                <div className="text-muted small">
                                    CSV files from a previous run
                                </div>
                            </>
                        )}
                    </div>
                    <input
                        ref={fileInput}
                        type="file"
                        accept=".csv,text/csv"
                        multiple
                        className="d-none"
                        onChange={e => e.target.files && addFiles(e.target.files)}
                    />
                    {touched && errors.file && (
                        <FormText className="text-danger">{errors.file}</FormText>
                    )}
                </fieldset>

                <div>
                    <Button color="primary" outline type="submit" disabled={uploading}>
                        {uploading && <Spinner size="sm" className="me-2" />}
                        {uploading
                            ? 'Loading…'
                            : `Load ${files.length > 1 ? 'the sheets' : 'the sheet'}`}
                    </Button>
                </div>
            </Form>
        </div>
    )
}

export default UploadForm
